import { PropertyFilterQuery } from '@cloudscape-design/collection-hooks';
import { DateRangePickerProps } from '@cloudscape-design/components';

import { getApiClient } from '../../api/client';
import type { components } from '../../api/schema';
import { buildListQueryParams, withHiddenFilter } from './query';

type PlaceListCreate = components['schemas']['PlaceListCreate'];
type PlaceRequest = components['schemas']['PlaceRequest'];

export async function getLists(
  query: PropertyFilterQuery,
  page = 1,
  limit = 20,
  range?: DateRangePickerProps.Value | null,
  now = new Date(),
) {
  const client = getApiClient();
  const params = buildListQueryParams({
    query: withHiddenFilter(query),
    page, limit, range, now,
    rangeName: 'created_at',
    sortingField: 'created_at',
    sortDirection: 'desc',
  });
  const { data, error } = await client.GET('/lists', { params: { query: params } });
  if (error) throw new Error(`Failed to fetch lists: ${JSON.stringify(error)}`);
  return data!;
}

export async function getAllLists() {
  const meta = await getListMeta();
  const emptyQuery: PropertyFilterQuery = {
    tokens: [{ propertyKey: 'hidden', operator: '=', value: 'none' }],
    operation: 'and',
  };
  const lists: NonNullable<Awaited<ReturnType<typeof getLists>>>['items'] = [];
  for (let i = 1; i <= Math.ceil(meta.total / 100); i++) {
    const page = await getLists(emptyQuery, i, 100);
    lists.push(...page.items);
  }
  return lists;
}

export async function getListMeta() {
  const client = getApiClient();
  const { data, error } = await client.GET('/lists/meta');
  if (error) throw new Error(`Failed to fetch lists meta: ${JSON.stringify(error)}`);
  return data!;
}

export async function getList(uuid: string) {
  const client = getApiClient();
  const { data, error } = await client.GET('/lists/{uuid}', { params: { path: { uuid } } });
  if (error) throw new Error(`Failed to fetch list: ${JSON.stringify(error)}`);
  return data;
}

export async function createList(body: PlaceListCreate) {
  const client = getApiClient();
  const { data, error } = await client.PUT('/lists', { body });
  if (error) throw new Error(`Failed to create list: ${JSON.stringify(error)}`);
  return data;
}

export async function deleteList(uuid: string) {
  const client = getApiClient();
  const { error } = await client.DELETE('/lists/{uuid}', { params: { path: { uuid } } });
  if (error) throw new Error(`Failed to delete list: ${JSON.stringify(error)}`);
}

export async function addPlacesToList(uuid: string, body: PlaceRequest) {
  const client = getApiClient();
  const { error } = await client.POST('/lists/{uuid}/places', { params: { path: { uuid } }, body });
  if (error) throw new Error(`Failed to add places: ${JSON.stringify(error)}`);
}

export async function removePlacesFromList(uuid: string, body: PlaceRequest) {
  const client = getApiClient();
  const { error } = await client.DELETE('/lists/{uuid}/places', { params: { path: { uuid } }, body });
  if (error) throw new Error(`Failed to remove places: ${JSON.stringify(error)}`);
}

export async function hideList(uuid: string) {
  const client = getApiClient();
  const { error } = await client.POST('/lists/{uuid}/hide', { params: { path: { uuid } } });
  if (error) throw new Error(`Failed to hide list: ${JSON.stringify(error)}`);
}

export async function unhideList(uuid: string) {
  const client = getApiClient();
  const { error } = await client.POST('/lists/{uuid}/unhide', { params: { path: { uuid } } });
  if (error) throw new Error(`Failed to unhide list: ${JSON.stringify(error)}`);
}

export async function hideLists(uuids: string[]) {
  for (const uuid of uuids) await hideList(uuid);
}

export async function unhideLists(uuids: string[]) {
  for (const uuid of uuids) await unhideList(uuid);
}
